// Roving-tabindex keyboard navigation for the feedback list
// (docs/06-ux-rules.md "Layout" / "Keyboard", P10 D5). No precedent for
// this pattern exists elsewhere in the repo (P10) — net new.
//
// Only one item in the list is ever a Tab stop (`tabIndex={0}`); every other
// item gets `tabIndex={-1}`. Arrow keys move which item is the Tab stop and
// move DOM focus to it; Enter/Space "select" the currently-focused item via
// `onSelect`.
import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface RovingListNavItemProps {
  tabIndex: 0 | -1
  'aria-selected': boolean
  onKeyDown: (event: KeyboardEvent) => void
  onClick: () => void
  ref: (node: HTMLElement | null) => void
}

export interface UseRovingListNavResult {
  activeIndex: number
  setActiveIndex: (index: number) => void
  getItemProps: (index: number) => RovingListNavItemProps
  getListProps: () => { role: 'listbox' }
}

export function useRovingListNav(
  itemCount: number,
  onSelect: (index: number) => void
): UseRovingListNavResult {
  const [activeIndex, setActiveIndexState] = useState(0)
  // A `useRef` (not a plain local array) so every closure — regardless of
  // which render created it — reads/writes the SAME array instance across
  // the component's lifetime; a plain array would be a fresh, disconnected
  // instance per render, so a memoized `setActiveIndex` from an earlier
  // render would never see refs registered by a later render's items.
  const itemRefs = useRef<(HTMLElement | null)[]>([])

  const setActiveIndex = useCallback(
    (index: number) => {
      if (itemCount === 0) return
      const clamped = Math.max(0, Math.min(index, itemCount - 1))
      setActiveIndexState(clamped)
      itemRefs.current[clamped]?.focus()
    },
    [itemCount]
  )

  // Nothing else watches itemCount shrinking — e.g. a filter change, or
  // PR 04's resolve animation removing a row — so a stale activeIndex left
  // pointing past the new last item means getItemProps never assigns
  // tabIndex={0} to any rendered item and the list becomes unreachable by
  // keyboard until the user clicks or arrows manually. Clamp directly via
  // the state setter (not the public setActiveIndex above) so a removal
  // that happens without user interaction doesn't also yank DOM focus onto
  // whatever item lands at the new index; clamping is not a selection, so
  // this never calls onSelect either.
  useEffect(() => {
    setActiveIndexState(prev => Math.min(prev, Math.max(0, itemCount - 1)))
  }, [itemCount])

  const getItemProps = useCallback(
    (index: number): RovingListNavItemProps => ({
      tabIndex: index === activeIndex ? 0 : -1,
      'aria-selected': index === activeIndex,
      ref: node => {
        itemRefs.current[index] = node
      },
      onClick: () => {
        setActiveIndex(index)
        onSelect(index)
      },
      onKeyDown: event => {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault()
            setActiveIndex(index + 1)
            break
          case 'ArrowUp':
            event.preventDefault()
            setActiveIndex(index - 1)
            break
          case 'Home':
            event.preventDefault()
            setActiveIndex(0)
            break
          case 'End':
            event.preventDefault()
            setActiveIndex(itemCount - 1)
            break
          case 'Enter':
          case ' ':
            event.preventDefault()
            setActiveIndex(index)
            onSelect(index)
            break
          default:
            break
        }
      },
    }),
    [activeIndex, itemCount, onSelect, setActiveIndex]
  )

  const getListProps = useCallback(() => ({ role: 'listbox' as const }), [])

  return { activeIndex, setActiveIndex, getItemProps, getListProps }
}
